import { Pressable } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";

export type WorklistFilter = "missing" | "all";

interface WorklistFilterToggleProps {
  value: WorklistFilter;
  onChange: (next: WorklistFilter) => void;
  missingCount: number;
  totalCount: number;
}

/**
 * Segmented "Missing only / All" filter. Operators overwhelmingly want the
 * missing-only view (that's the work), so "missing" is the default the screen
 * picks — but only when there's actually something missing (see the screen's
 * data-driven default). Counts are surfaced inline so the operator can see the
 * size of each bucket before switching.
 */
export function WorklistFilterToggle({ value, onChange, missingCount, totalCount }: WorklistFilterToggleProps) {
  const { theme } = useUnistyles();
  return (
    <View
      style={{
        flexDirection: "row",
        borderWidth: 1,
        borderColor: theme.colors.borderCard,
        borderRadius: theme.spacing.md,
        overflow: "hidden",
        alignSelf: "flex-start",
      }}
    >
      <Option
        label="Missing only"
        count={missingCount}
        selected={value === "missing"}
        first
        onPress={() => onChange("missing")}
      />
      <Option label="All" count={totalCount} selected={value === "all"} onPress={() => onChange("all")} />
    </View>
  );
}

interface OptionProps {
  label: string;
  count: number;
  selected: boolean;
  first?: boolean;
  onPress: () => void;
}

function Option({ label, count, selected, first, onPress }: OptionProps) {
  const { theme } = useUnistyles();
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing.sm,
        paddingVertical: theme.spacing.lg,
        paddingHorizontal: theme.spacing["3xl"],
        backgroundColor: selected ? theme.colors.icon : "transparent",
        borderLeftWidth: first ? 0 : 1,
        borderLeftColor: theme.colors.borderCard,
      }}
    >
      <Text textStyle={selected ? "sm/semibold" : "sm/regular"} color={selected ? "background" : "text"}>
        {label}
      </Text>
      <Text textStyle="xs/medium" style={{ color: selected ? theme.colors.background : theme.colors.textSecondary }}>
        {count}
      </Text>
    </Pressable>
  );
}
